import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Alert } from '../../../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { qk } from '../../../lib/queryKeys';
import {
  sistemaApi,
  type SistemaDatos,
  type SistemaExposicion,
  type SistemaItem,
  type SistemaLinea,
  type Veredicto,
} from '../api/sistema.api';

// Sistema (ciclo-panel, R5): lo que Javier mira del VPS. Es la pantalla técnica
// detrás de "Configuración"; Ailen no la ve. Los textos de las piezas (qué hace
// cada cosa, las trampas) vienen del inventario del repo hermano, en español.

type Vista = 'lineas' | 'tipos' | 'exposicion' | 'avisos';
const DATOS_VIEJOS_MIN = 120;

export function SistemaAdminPage(): JSX.Element {
  const es = useI18n();
  const t = es.sistema;
  const [vista, setVista] = useState<Vista>('lineas');
  const [sel, setSel] = useState<string | null>(null);
  const { data, isLoading, error } = useQuery({ queryKey: qk.admin.sistema, queryFn: sistemaApi.obtener });

  const d = data?.datos ?? null;
  const nMal = d ? d.lineas.reduce((n, l) => n + l.n_mal, 0) : 0;
  const altos = d ? d.avisos.filter((a) => a.nivel === 'alto').length : 0;

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h1 className="font-titulos text-3xl font-bold text-marca-primario-oscuro">{t.titulo}</h1>
        {d && (
          <span className="text-sm text-text-subtle">
            {d.host} · {d.ip} · {t.tomado.replace('{min}', String(data?.tomadoHaceMin ?? '-'))}
          </span>
        )}
      </div>
      <p className="mt-1 text-sm text-text-subtle">{t.intro}</p>

      {error && (
        <Alert variant="error" className="mt-4">
          {getErrorMessage(error, es.errores.generico.algoSalioMal)}
        </Alert>
      )}
      {isLoading && <p className="mt-6 text-sm text-text-subtle">{es.app.cargando}</p>}
      {data && !data.disponible && (
        <Alert variant="warning" className="mt-4" title={t.sinDatos}>
          {data.motivo}
        </Alert>
      )}
      {data?.disponible && (data.tomadoHaceMin ?? 0) > DATOS_VIEJOS_MIN && (
        <Alert variant="warning" className="mt-4">
          {t.viejo}
        </Alert>
      )}

      {d && (
        <>
          <Kpis d={d} nMal={nMal} />
          <Tabs value={vista} onChange={(v) => setVista(v as Vista)} className="mt-6">
            <TabsList>
              <TabsTrigger value="lineas">
                {t.pestanas.lineas} <Contador n={nMal} tono="mal" />
              </TabsTrigger>
              <TabsTrigger value="tipos">{t.pestanas.tipos}</TabsTrigger>
              <TabsTrigger value="exposicion">
                {t.pestanas.exposicion} <Contador n={d.kpis.expuestos || d.kpis.una_capa} tono={d.kpis.expuestos ? 'mal' : 'amb'} />
              </TabsTrigger>
              <TabsTrigger value="avisos">
                {t.pestanas.avisos} <Contador n={d.avisos.length} tono={altos ? 'mal' : 'amb'} />
              </TabsTrigger>
            </TabsList>
            <TabsContent value="lineas">
              <p className="mb-3 text-xs text-text-subtle">{t.lineas.ayuda}</p>
              <VistaLineas d={d} sel={sel} onSel={setSel} />
            </TabsContent>
            <TabsContent value="tipos">
              <VistaTipos d={d} />
            </TabsContent>
            <TabsContent value="exposicion">
              <VistaExposicion d={d} />
            </TabsContent>
            <TabsContent value="avisos">
              <VistaAvisos d={d} />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}

// ─── piezas ──────────────────────────────────────────────────────────

function Punto({ estado, className }: { estado: Veredicto | 'amb' | null | undefined; className?: string }): JSX.Element {
  return (
    <span
      className={cn(
        'inline-block h-2.5 w-2.5 shrink-0 rounded-full',
        estado === 'ok' && 'bg-success',
        estado === 'mal' && 'bg-danger',
        estado === 'amb' && 'bg-warning',
        (estado === 'sin_dato' || !estado) && 'bg-marca-gris-medio/60',
        className,
      )}
    />
  );
}

function Etiqueta({ children, className }: { children: ReactNode; className?: string }): JSX.Element {
  return (
    <span className={cn('inline-flex items-center rounded border border-border-subtle bg-surface-muted px-1.5 text-[11px] leading-5 text-text-subtle', className)}>
      {children}
    </span>
  );
}

function Contador({ n, tono }: { n: number; tono: 'mal' | 'amb' }): JSX.Element | null {
  if (!n) return null;
  return (
    <span className={cn('ml-1.5 inline-flex min-w-[18px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold text-white', tono === 'mal' ? 'bg-danger' : 'bg-warning')}>
      {n}
    </span>
  );
}

function Kpis({ d, nMal }: { d: SistemaDatos; nMal: number }): JSX.Element {
  const t = useI18n().sistema.kpis;
  const k = d.kpis;
  const tiles: { v: string | number; e: string; tono?: 'ok' | 'mal' | 'amb' | undefined }[] = [
    { v: `${String(k.contenedores[0])}/${String(k.contenedores[1])}`, e: t.contenedores },
    { v: `${String(k.workflows[0])}/${String(k.workflows[1])}`, e: t.workflows },
    { v: nMal, e: t.fuera, tono: nMal ? 'mal' : 'ok' },
    { v: k.publicos, e: t.publicos, tono: k.expuestos ? 'mal' : undefined },
    { v: k.una_capa, e: t.unaCapa, tono: k.una_capa ? 'amb' : 'ok' },
    { v: `${String(k.disco_pct ?? '-')}%`, e: `${t.disco} · ${k.disco}`, tono: (k.disco_pct ?? 0) >= d.umbral_disco ? 'mal' : undefined },
    { v: k.ram, e: `${t.ram} ${k.load}` },
    { v: `${String(k.uptime_d)} d`, e: t.uptime },
    { v: k.leads ?? '-', e: `${t.leads} · +${String(k.leads_24h ?? '-')} / +${String(k.leads_7d ?? '-')}` },
    { v: k.backup_dias == null ? '-' : `${String(k.backup_dias)} d`, e: t.backup, tono: (k.backup_dias ?? 0) > 30 ? 'amb' : undefined },
  ];
  return (
    <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      {tiles.map((x) => (
        <div key={x.e} className="rounded-lg border border-border-subtle bg-surface px-4 py-2.5 shadow-card">
          <div className={cn('font-titulos text-xl font-semibold leading-tight text-text-strong', x.tono === 'mal' && 'text-danger', x.tono === 'amb' && 'text-warning', x.tono === 'ok' && 'text-success')}>
            {x.v}
          </div>
          <div className="truncate text-xs text-text-subtle">{x.e}</div>
        </div>
      ))}
    </div>
  );
}

function FilaItem({ item: it, linea }: { item: SistemaItem; linea?: SistemaLinea }): JSX.Element {
  const t = useI18n().sistema.lineas;
  const [abierto, setAbierto] = useState(false);
  return (
    <div className="border-b border-border-subtle last:border-0">
      <button type="button" onClick={() => setAbierto(!abierto)} className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-surface-alt">
        <Punto estado={it.veredicto} />
        {linea && <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: linea.color }} title={linea.nombre} />}
        <span className="truncate font-medium text-text-strong">{it.nombre}</span>
        <Etiqueta>{it.tipo}</Etiqueta>
        {it.puertos && <Etiqueta className="hidden font-mono md:inline-flex">{it.puertos}</Etiqueta>}
        <span className={cn('ml-auto shrink-0 text-xs text-text-subtle', it.veredicto === 'mal' && 'font-semibold text-danger')}>
          {it.real ?? '—'}
          {it.veredicto === 'mal' && <span className="font-normal"> ({t.esperado} {it.esperado})</span>}
        </span>
        <ChevronDown size={14} className={cn('shrink-0 text-text-faint transition-transform', abierto && 'rotate-180')} />
      </button>
      {abierto && (
        <div className="flex flex-col gap-1 bg-surface-alt px-3 py-2 pl-9 text-xs">
          <div className="text-text-strong">{it.que_hace}</div>
          {it.detalle && <div className="text-text-subtle">{it.detalle}</div>}
          {it.trampa && (
            <div className="flex items-start gap-1.5 text-warning">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <span>{it.trampa}</span>
            </div>
          )}
          <div className="text-text-faint">
            <code className="font-mono">{it.clave}</code>
          </div>
        </div>
      )}
    </div>
  );
}

function VistaLineas({ d, sel, onSel }: { d: SistemaDatos; sel: string | null; onSel: (id: string | null) => void }): JSX.Element {
  const t = useI18n().sistema.lineas;
  const linea = d.lineas.find((l) => l.id === sel) ?? null;
  const nombre = (id: string): { n: string; c: string } => {
    const x = d.lineas.find((y) => y.id === id);
    return { n: x?.nombre ?? id, c: x?.color ?? 'inherit' };
  };
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <div className={cn('grid flex-1 gap-3 sm:grid-cols-2', !linea && 'xl:grid-cols-3')}>
        {d.lineas.map((l) => {
          const tipos = new Map<string, number>();
          l.items.forEach((i) => tipos.set(i.tipo, (tipos.get(i.tipo) ?? 0) + 1));
          return (
            <button
              key={l.id}
              type="button"
              onClick={() => onSel(sel === l.id ? null : l.id)}
              style={{ borderLeftColor: l.color }}
              className={cn(
                'flex flex-col gap-2 rounded-lg border border-l-4 border-border-subtle bg-surface p-4 text-left shadow-card transition-shadow hover:shadow-card-hover',
                sel === l.id && 'bg-surface-primario',
              )}
            >
              <div className="flex items-center gap-2">
                <Punto estado={l.salud} />
                <span className="font-medium leading-tight" style={{ color: l.color }}>
                  {l.nombre}
                </span>
                <span className="ml-auto shrink-0 text-xs text-text-subtle">
                  {l.n_ok}/{l.items.length}
                  {l.n_mal > 0 && <b className="text-danger"> · {l.n_mal}</b>}
                </span>
              </div>
              <div className="flex flex-wrap gap-1">
                {[...tipos].map(([tipo, n]) => (
                  <Etiqueta key={tipo}>
                    {n} {tipo}
                  </Etiqueta>
                ))}
              </div>
            </button>
          );
        })}
      </div>
      {linea && (
        <aside className="w-full shrink-0 rounded-lg border border-border-subtle bg-surface shadow-card lg:sticky lg:top-16 lg:w-[420px]">
          <div className="flex items-center gap-2 border-b border-border-subtle px-3 py-2.5">
            <h2 className="font-titulos text-base font-semibold" style={{ color: linea.color }}>
              {linea.nombre}
            </h2>
            <button type="button" onClick={() => onSel(null)} className="ml-auto rounded p-1 text-text-faint hover:bg-surface-muted" aria-label={t.cerrar}>
              <X size={16} />
            </button>
          </div>
          <p className="border-b border-border-subtle px-3 py-2 text-xs text-text-subtle">{linea.descripcion}</p>
          {linea.items.map((it) => (
            <FilaItem key={it.clave} item={it} />
          ))}
          <div className="flex flex-col gap-1 border-t border-border-subtle px-3 py-2 text-xs">
            {d.flujos
              .filter((f) => f.a === linea.id)
              .map((f, i) => (
                <div key={`r${String(i)}`}>
                  <span className="text-text-faint">{t.recibeDe} </span>
                  <b style={{ color: nombre(f.de).c }}>{nombre(f.de).n}</b>
                  {f.etiqueta && <span className="text-text-faint"> · {f.etiqueta}</span>}
                </div>
              ))}
            {d.flujos
              .filter((f) => f.de === linea.id)
              .map((f, i) => (
                <div key={`m${String(i)}`}>
                  <span className="text-text-faint">{t.mandaA} </span>
                  <b style={{ color: nombre(f.a).c }}>{nombre(f.a).n}</b>
                  {f.etiqueta && <span className="text-text-faint"> · {f.etiqueta}</span>}
                </div>
              ))}
            {d.externos.some((x) => x.usado_por.includes(linea.id)) && (
              <div className="mt-1 flex flex-wrap items-center gap-1 text-text-subtle">
                {t.externos}:
                {d.externos
                  .filter((x) => x.usado_por.includes(linea.id))
                  .map((x) => (
                    <Etiqueta key={x.id}>
                      <span title={x.detalle}>{x.nombre}</span>
                    </Etiqueta>
                  ))}
              </div>
            )}
          </div>
        </aside>
      )}
    </div>
  );
}

const GRUPOS: SistemaItem['tipo'][] = ['contenedor', 'workflow', 'servicio', 'puerto', 'instancia_wa'];

function VistaTipos({ d }: { d: SistemaDatos }): JSX.Element {
  const t = useI18n().sistema.tipos;
  const todos = d.lineas.flatMap((l) => l.items.map((i) => ({ i, l })));
  const peso = (x: { i: SistemaItem }): number => (x.i.veredicto === 'mal' ? 0 : x.i.esperado === 'activo' ? 1 : 2);
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {GRUPOS.map((tipo) => {
        const its = todos.filter((x) => x.i.tipo === tipo).sort((a, b) => peso(a) - peso(b));
        if (!its.length) return null;
        const activos = its.filter((x) => x.i.real === 'activo' || x.i.real === 'open').length;
        return (
          <section key={tipo} className={cn('rounded-lg border border-border-subtle bg-surface shadow-card', tipo === 'workflow' && 'md:row-span-2')}>
            <h3 className="flex items-baseline gap-2 border-b border-border-subtle px-3 py-2 text-sm font-semibold text-text-strong">
              {t[tipo]}
              <span className="text-xs font-normal text-text-subtle">{t.activosDe.replace('{n}', String(activos)).replace('{total}', String(its.length))}</span>
            </h3>
            {its.map((x) => (
              <FilaItem key={x.i.clave} item={x.i} linea={x.l} />
            ))}
          </section>
        );
      })}
    </div>
  );
}

const BANDAS: SistemaExposicion['nivel'][] = ['expuesto', 'una_capa', 'ok'];

function VistaExposicion({ d }: { d: SistemaDatos }): JSX.Element {
  const t = useI18n().sistema.exposicion;
  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-text-subtle">{t.intro}</p>
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-faint">{t.capas}</h3>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {d.capas.map((c) => (
            <div key={c.nombre} className="flex items-start gap-2 rounded-md border border-border-subtle bg-surface px-3 py-2 text-xs shadow-card">
              <Punto estado={c.estado === 'ok' ? 'ok' : c.estado === 'falta' ? 'mal' : 'amb'} className="mt-1" />
              <div>
                <div className="font-medium text-text-strong">{c.nombre}</div>
                <div className="text-text-subtle">{c.detalle}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
      {BANDAS.map((nivel) => {
        const fs = d.exposicion.filter((f) => f.nivel === nivel);
        if (!fs.length && nivel !== 'expuesto') return null;
        return (
          <section key={nivel}>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-faint">{t.bandas[nivel]}</h3>
            {fs.length === 0 ? (
              <p className="text-sm text-success">{t.ninguno}</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border-subtle bg-surface shadow-card">
                <table className="w-full text-sm">
                  <thead className="bg-surface-muted text-left text-[11px] uppercase tracking-wide text-text-subtle">
                    <tr>
                      <th className="px-3 py-2">{t.columnas.puerto}</th>
                      <th className="px-3 py-2">{t.columnas.servicio}</th>
                      <th className="px-3 py-2">{t.columnas.escucha}</th>
                      <th className="px-3 py-2">{t.columnas.separa}</th>
                      <th className="px-3 py-2">{t.columnas.sondeo}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fs.map((f) => (
                      <tr key={`${f.puerto}/${f.proto}`} className="border-t border-border-subtle even:bg-surface-alt">
                        <td className={cn('px-3 py-1.5 font-mono', f.nivel === 'expuesto' && 'font-bold text-danger', f.nivel === 'una_capa' && 'font-semibold text-warning')}>
                          {f.puerto}/{f.proto}
                        </td>
                        <td className="px-3 py-1.5">{f.proceso}</td>
                        <td className="px-3 py-1.5 text-text-subtle">
                          {f.interfaz === 'todas' ? t.todas : f.interfaz === 'local' ? t.local : t.privada}
                          {f.docker && ' · Docker'}
                        </td>
                        <td className="px-3 py-1.5">{f.capas}</td>
                        <td className="px-3 py-1.5">
                          {f.sondeo === 'abierto' ? <b className="text-danger">{t.abierto}</b> : <span className="text-text-subtle">{f.sondeo}</span>}
                          {f.esperado_publico && <span className="text-text-faint"> · {t.previsto}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function VistaAvisos({ d }: { d: SistemaDatos }): JSX.Element {
  const t = useI18n().sistema.avisos;
  if (!d.avisos.length) return <p className="text-sm text-success">{t.vacio}</p>;
  return (
    <ul className="flex max-w-4xl flex-col gap-2">
      {d.avisos.map((a, i) => (
        <li key={String(i)} className="flex items-start gap-3 rounded-lg border border-border-subtle bg-surface px-3 py-2.5 text-sm shadow-card">
          <span className={cn('mt-0.5 w-12 shrink-0 text-[11px] font-bold uppercase', a.nivel === 'alto' && 'text-danger', a.nivel === 'medio' && 'text-warning', a.nivel === 'bajo' && 'text-text-faint')}>
            {a.nivel}
          </span>
          <span className="text-text-strong">{a.texto}</span>
        </li>
      ))}
    </ul>
  );
}
